import { lookupTerm, type TermDefinition } from '../vocabulary/index.js';

/**
 * Reading the interchange dialect back.
 *
 * The dialect used to be export-only, and that was the defect: the export a user actually
 * downloads is the compliant one, so refusing to import it would have meant handing someone a
 * file the studio itself will not read. Nothing about the fold prevents the reverse — every
 * predicate keeps its own name and arity in `attributes`, and `dpemType` keeps the ontology
 * class — so the conversion is mechanical and total:
 *
 *   `id`                 → `@id`
 *   `attributes.dpemType` → `@type`
 *   `attributes.<term>`   → the term, retyped from its string by the vocabulary
 *   `relatesTo`           → dropped
 *
 * `relatesTo` is dropped rather than merged because it is the *flattened* index: it says a node
 * points at `gate:G5` but not that the predicate was `governedByGate`. Every edge in it is
 * already in `attributes` under its own predicate (R6.2), which is the copy that carries
 * meaning. Reading both would either duplicate edges or invent a predicate for them.
 *
 * Nothing here validates. Unknown keys and undecodable values are passed through as the strings
 * they were, so `normaliseDocument` and the node schemas report them the way they report any
 * other bad input — one place that judges a document, not two.
 */

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Whether these nodes are written in the interchange dialect.
 *
 * `@id` is the discriminator, and its *absence* is what counts: a canonical node always has
 * one, a dialect node never does. `attributes.dpemType` is accepted as the same signal for a
 * node that carries no id at all, which is malformed either way — recognising the dialect
 * first means the error it gets is about the missing id rather than about `attributes` being an
 * unknown term.
 */
export function isInteropNodeList(nodes: readonly unknown[]): boolean {
  return nodes.some((node) => {
    if (!isPlainObject(node)) return false;
    if ('@id' in node || '@type' in node) return false;
    if (typeof node.id === 'string') return true;
    const attributes = node.attributes;
    return isPlainObject(attributes) && typeof attributes.dpemType === 'string';
  });
}

/** The ontology class the node came from, which `type` alone cannot say. */
const ONTOLOGY_TYPE_KEY = 'dpemType';

/** Set by the serialiser when `name` was derived from the id; see `interop.ts`. */
const DERIVED_NAME_KEY = 'nameFromId';

/** Attribute keys the dialect owns. They describe the encoding, not the method. */
const DIALECT_KEYS: readonly string[] = [ONTOLOGY_TYPE_KEY, DERIVED_NAME_KEY];

/** Node-level keys; anything else on a dialect node is not a property of the node. */
const NODE_KEYS: readonly string[] = ['id', 'type', 'name', 'description', 'relatesTo'];

function decodeScalar(term: TermDefinition | undefined, value: string): unknown {
  if (term === undefined) return value;
  if (term.kind === 'number') {
    const parsed = Number(value);
    return value.trim().length > 0 && Number.isFinite(parsed) ? parsed : value;
  }
  if (term.kind === 'boolean') {
    if (value === 'true') return true;
    if (value === 'false') return false;
    return value;
  }
  return value;
}

/** `"[\"role:qa\", \"role:pm\"]"` → `['role:qa', 'role:pm']`, or `undefined` if it is not one. */
function decodeList(value: string): readonly unknown[] | undefined {
  if (!value.startsWith('[')) return undefined;
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function decodeAttribute(key: string, value: unknown): unknown {
  const term = lookupTerm(key);
  if (typeof value !== 'string') return value;

  const list = decodeList(value);
  if (list !== undefined) {
    return list.map((item) => (typeof item === 'string' ? decodeScalar(term, item) : item));
  }
  return decodeScalar(term, value);
}

/**
 * One dialect node as a canonical node, ready for `normaliseDocument`.
 *
 * `type` is ignored on purpose. It is the dialect's three-value approximation of the class, and
 * `dpemType` is the class itself; preferring the approximation would turn every `dpem:Stage`
 * into an `Entity` on the way back in.
 */
export function canonicalNodeFromInterop(node: Record<string, unknown>): Record<string, unknown> {
  const canonical: Record<string, unknown> = {};

  if ('id' in node) canonical['@id'] = node.id;
  const attributes = isPlainObject(node.attributes) ? node.attributes : {};
  if (ONTOLOGY_TYPE_KEY in attributes) canonical['@type'] = attributes[ONTOLOGY_TYPE_KEY];

  const derivedName = attributes[DERIVED_NAME_KEY] === 'true';
  if (typeof node.name === 'string' && !derivedName) canonical.name = node.name;
  if (typeof node.description === 'string') canonical.description = node.description;

  for (const [key, value] of Object.entries(attributes)) {
    if (DIALECT_KEYS.includes(key)) continue;
    canonical[key] = decodeAttribute(key, value);
  }

  // A key the dialect does not define is not silently dropped: it survives under its own name
  // so the parser reports it as an unknown term rather than losing an author's data.
  for (const [key, value] of Object.entries(node)) {
    if (NODE_KEYS.includes(key) || key === 'attributes') continue;
    if (!(key in canonical)) canonical[key] = value;
  }

  return canonical;
}
